import { useEffect, useRef, useState, useCallback } from 'react';
import api, { formatINR } from '@/lib/api';
import { toast } from 'sonner';
import { BellRinging, X, WhatsappLogo, SpeakerHigh } from '@phosphor-icons/react';

// WhatsApp notification recipient (business owner). Uses free wa.me click-to-chat
// link — no paid WhatsApp Business API is used.
const WHATSAPP_TO = '919971236105';
const POLL_MS = 15000;
const BEEP_DURATION_MS = 5000;
const LS_KEY = 'zk_admin_last_seen_order';
const LS_NOTIFIED_KEY = 'zk_admin_notified_orders'; // dedup WhatsApp opens
const LS_SOUND_ENABLED = 'zk_admin_sound_enabled';

function buildWaLink(order) {
  const lines = [
    `NEW ORDER  ${order.order_no}`,
    `Customer: ${order.customer_name || order.delivery_address?.name || '-'}`,
    `Mobile: ${order.customer_mobile || order.delivery_address?.phone || '-'}`,
    `Amount: INR ${order.total}`,
    `Payment: ${order.payment_method || 'COD'}`,
    order.delivery_address?.pincode ? `Pincode: ${order.delivery_address.pincode}` : '',
  ].filter(Boolean).join('\n');
  return `https://wa.me/${WHATSAPP_TO}?text=${encodeURIComponent(lines)}`;
}

export default function NewOrderAlert() {
  const [newOrder, setNewOrder] = useState(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const audioCtxRef = useRef(null);
  const stopBeepRef = useRef(null);
  const lastSeenRef = useRef(localStorage.getItem(LS_KEY) || '');
  const soundEnabledRef = useRef(localStorage.getItem(LS_SOUND_ENABLED) === '1');

  const stopBeep = useCallback(() => {
    if (stopBeepRef.current) {
      try { stopBeepRef.current(); } catch (e) {}
      stopBeepRef.current = null;
    }
  }, []);

  const playBeep = useCallback(async () => {
    try {
      if (!audioCtxRef.current) {
        // eslint-disable-next-line no-undef
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return false;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch (e) { return false; }
      }
      if (ctx.state !== 'running') return false;
      stopBeep();
      const gain = ctx.createGain();
      gain.gain.value = 0.35;
      gain.connect(ctx.destination);
      // Alternating two-tone alarm (like ambulance) to be loud & unmissable.
      const osc = ctx.createOscillator();
      osc.type = 'square';
      const now = ctx.currentTime;
      const endAt = now + BEEP_DURATION_MS / 1000;
      // Ramp between 880Hz and 1200Hz every 250ms for 5s
      let t = now;
      let hi = true;
      while (t < endAt) {
        osc.frequency.setValueAtTime(hi ? 1200 : 880, t);
        t += 0.25;
        hi = !hi;
      }
      osc.connect(gain);
      osc.start(now);
      osc.stop(endAt);
      stopBeepRef.current = () => {
        try { osc.stop(); } catch (e) {}
        try { osc.disconnect(); } catch (e) {}
        try { gain.disconnect(); } catch (e) {}
      };
      return true;
    } catch (e) {
      return false;
    }
  }, [stopBeep]);

  const enableSound = useCallback(async () => {
    const ok = await playBeep();
    if (ok) {
      soundEnabledRef.current = true;
      localStorage.setItem(LS_SOUND_ENABLED, '1');
      setSoundBlocked(false);
      // Immediately stop the "unlock" beep after ~200ms.
      setTimeout(() => stopBeep(), 200);
      toast.success('New-order sound enabled');
    } else {
      toast.error('Could not enable sound. Check browser audio permissions.');
    }
  }, [playBeep, stopBeep]);

  const dismiss = useCallback(() => {
    stopBeep();
    setNewOrder(null);
  }, [stopBeep]);

  useEffect(() => {
    let cancelled = false;

    const handleNewOrder = async (order) => {
      // Dedup: never alert twice for the same order_no (survives reloads).
      let notified = [];
      try { notified = JSON.parse(localStorage.getItem(LS_NOTIFIED_KEY) || '[]'); } catch (e) { notified = []; }
      if (notified.includes(order.order_no)) return;
      notified = [order.order_no, ...notified].slice(0, 100);
      localStorage.setItem(LS_NOTIFIED_KEY, JSON.stringify(notified));

      setNewOrder(order);
      try { navigator.vibrate?.([400, 150, 400, 150, 600]); } catch (e) {}

      // Browser notification (best-effort)
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification(`New order ${order.order_no}`, {
            body: `${order.customer_name || 'Customer'} - INR ${order.total}`,
            tag: order.order_no,
            requireInteraction: true,
          });
        } catch (e) {}
      }

      // 5s loud beep (falls back to Enable Sound button if autoplay blocked)
      const played = await playBeep();
      if (!played) setSoundBlocked(true);

      // Auto-open WhatsApp click-to-chat once per new order (best-effort).
      try {
        const win = window.open(buildWaLink(order), '_blank', 'noopener,noreferrer');
        if (!win) {
          // Popup blocked — user can still click the button in the banner.
        }
      } catch (e) {}
    };

    const tick = async () => {
      try {
        const { data } = await api.get('/orders/admin/all?status=placed&limit=5');
        if (cancelled) return;
        if (!Array.isArray(data) || data.length === 0) return;
        const newest = data[0];
        // Bootstrap: first ever poll — remember newest and don't alert.
        if (!lastSeenRef.current) {
          lastSeenRef.current = newest.order_no;
          localStorage.setItem(LS_KEY, newest.order_no);
          return;
        }
        if (newest.order_no !== lastSeenRef.current) {
          const fresh = [];
          for (const o of data) {
            if (o.order_no === lastSeenRef.current) break;
            fresh.push(o);
          }
          if (fresh.length > 0) {
            // Alert for the newest unseen order (show most-recent in banner).
            await handleNewOrder(fresh[0]);
          }
          lastSeenRef.current = newest.order_no;
          localStorage.setItem(LS_KEY, newest.order_no);
        }
      } catch (e) { /* ignore transient errors */ }
    };

    if ('Notification' in window && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch (e) {}
    }

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [playBeep]);

  return (
    <>
      {/* Enable sound helper — appears once when browser blocks autoplay */}
      {soundBlocked && !newOrder && (
        <button
          data-testid="admin-enable-sound-btn"
          onClick={enableSound}
          className="fixed top-4 right-4 z-[60] inline-flex items-center gap-2 h-10 px-4 rounded-full bg-amber-500 text-white text-sm font-semibold shadow-lg hover:bg-amber-600"
        >
          <SpeakerHigh size={16} weight="bold"/> Enable order sound
        </button>
      )}

      {newOrder && (
        <div
          data-testid="admin-new-order-banner"
          className="fixed inset-x-0 top-0 z-[70] bg-terracotta-600 text-white shadow-2xl animate-pulse"
          role="alert"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center gap-3">
            <BellRinging size={28} weight="fill" className="flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-lg leading-tight">
                NEW ORDER{' '}
                <span data-testid="new-order-no" className="font-bold">{newOrder.order_no}</span>
                {' · '}
                <span data-testid="new-order-amount">{formatINR(newOrder.total)}</span>
              </p>
              <p className="text-xs text-white/80 truncate">
                {newOrder.customer_name || newOrder.delivery_address?.name || 'Customer'}
                {' · '}
                {newOrder.customer_mobile || newOrder.delivery_address?.phone || '-'}
              </p>
            </div>
            {soundBlocked && (
              <button
                data-testid="new-order-enable-sound"
                onClick={enableSound}
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600"
              >
                <SpeakerHigh size={14} weight="bold"/> Enable sound
              </button>
            )}
            <a
              data-testid="new-order-whatsapp-btn"
              href={buildWaLink(newOrder)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white text-terracotta-700 text-xs font-semibold hover:bg-cream-100"
            >
              <WhatsappLogo size={14} weight="fill"/> WhatsApp
            </a>
            <button
              data-testid="new-order-dismiss"
              onClick={dismiss}
              aria-label="Dismiss"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"
            >
              <X size={18} weight="bold"/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
